import { Head, Link, router, usePage } from "@inertiajs/react"
import {
  BookOpen,
  Plus,
  Minus,
  Users,
  Search,
  Layers,
  Loader2,
  ChevronRight,
  ChevronLeft,
  EyeOff,
  FileText,
  Download,
  Link as LinkIcon,
  Video,
  HelpCircle,
  Paperclip,
  Pencil,
  Globe,
  Clock,
  XCircle,
  Eye,
  Trash2,
  Filter,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  Archive,
  ArchiveRestore,
  MoreHorizontal,
} from "lucide-react"
import { useEffect, useMemo, useState } from "react"
import { CoachLayout } from "../components/CoachLayout"
import { CoachGroupSwitcher } from "../components/CoachGroupSwitcher"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { cn, inertiaUrlTab } from "@/lib/utils"
import { CourseCoverImage } from "@/components/CourseCoverImage"
import { sanitizeContentHtml } from "@/lib/sanitize-html"
import {
  allCategories,
  buildSearchGroups,
  matchesCourse,
  matchesTrack,
  myCourseWorkflowKey,
} from "@/lib/content-search"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { SortableHeader } from "@/components/ui/sortable-header"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

// ---------------------------------------------------------------------------
// Filtering / sorting helpers
// ---------------------------------------------------------------------------

function uniqueCategoriesFromCourses(courses) {
  const map = new Map()
  for (const c of courses) {
    if (c.category?.id != null) {
      map.set(String(c.category.id), c.category)
    }
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
}

// A date column opens newest first (`firstDirection` "desc"); every other
// column opens ascending. The third click returns to the table's default.
function sortCycleThreeState(
  sortKey,
  setSortKey,
  direction,
  setDirection,
  col,
  defaultKey = "title",
  defaultDirection = "asc",
  firstDirection = "asc"
) {
  if (sortKey !== col) {
    setSortKey(col)
    setDirection(firstDirection)
  } else if (direction === firstDirection) {
    setDirection(firstDirection === "asc" ? "desc" : "asc")
  } else {
    setSortKey(defaultKey)
    setDirection(defaultDirection)
  }
}

const DATE_COLUMNS = new Set(["assignedAt", "archivedAt"])

// #649 — "Oct 9, 2026". Month, day and year, so two courses sent the same
// month are easy to tell apart.
function formatShortDate(iso) {
  if (!iso) return ""
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

// ISO 8601 strings from one serializer compare correctly as strings.
function cmpDate(a, b, direction) {
  return cmpStr(a || "", b || "", direction)
}

function ArchivedBadge() {
  return (
    <Badge variant="outline" className="text-[10px] font-medium text-zinc-500 border-zinc-300 ml-2 align-middle">
      Archived
    </Badge>
  )
}

function DateCell({ iso }) {
  return iso ? (
    <span className="text-sm text-zinc-600 whitespace-nowrap tabular-nums">{formatShortDate(iso)}</span>
  ) : (
    <span className="text-xs text-zinc-400">—</span>
  )
}

// Row "⋯" menu for the Content tables. Progress stays a visible button; the
// state-changing actions live here, each with one line saying what it does to
// members. Archive and Restore take effect on one click, no confirm: both are
// undone by the other. Remove keeps its own behavior.
const MENU_HINTS = {
  archive: "Leaves your Assigned list. Members keep access under Past Courses.",
  restore: "Back to your Assigned list. Members see it as current again.",
  removeCourse: "Members lose access to this course.",
  removeTrack: "Members lose every course in this track.",
}

function RowActionsMenu({ label, isLoading, progressHref, onArchive, onRestore, onRemove, removeHint }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-zinc-500"
          disabled={isLoading}
          aria-label={label}
          title="More actions"
        >
          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        {progressHref && (
          <DropdownMenuItem className="sm:hidden" onSelect={() => router.visit(progressHref)}>
            <Users className="h-4 w-4" />
            View progress
          </DropdownMenuItem>
        )}
        {onArchive && (
          <MenuItemWithHint icon={Archive} title="Archive" hint={MENU_HINTS.archive} onSelect={onArchive} />
        )}
        {onRestore && (
          <MenuItemWithHint icon={ArchiveRestore} title="Restore" hint={MENU_HINTS.restore} onSelect={onRestore} />
        )}
        {onRemove && (
          <MenuItemWithHint
            icon={Trash2}
            title="Remove from group"
            hint={removeHint}
            onSelect={onRemove}
            destructive
          />
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function MenuItemWithHint({ icon: Icon, title, hint, onSelect, destructive = false }) {
  return (
    <DropdownMenuItem
      variant={destructive ? "destructive" : "default"}
      className="items-start"
      onSelect={onSelect}
    >
      <Icon className="h-4 w-4 mt-0.5" />
      <div className="min-w-0">
        <p className="font-medium">{title}</p>
        <p className="text-xs text-zinc-500 whitespace-normal">{hint}</p>
      </div>
    </DropdownMenuItem>
  )
}

function cmpNum(a, b, direction) {
  if (a === b) return 0
  const n = a - b
  return direction === "asc" ? n : -n
}

function cmpStr(a, b, direction) {
  if (!a && !b) return 0
  if (!a) return 1
  if (!b) return -1
  const c = a.localeCompare(b)
  return direction === "asc" ? c : -c
}

function courseProgressRatio(course) {
  const p = course.progress
  if (!p || p.totalMembers <= 0) return -1
  return p.completedMembers / p.totalMembers
}

function myCourseStatusOrdinal(course) {
  if (course.status === "draft") return 0
  const ps = course.publicStatus || "private"
  const order = { pending_review: 1, public: 2, private: 3, rejected: 4 }
  return order[ps] ?? 99
}

function categoriesFromCoursesAndTracks(courses, tracks) {
  const map = new Map()
  for (const c of courses) {
    if (c.category?.id != null) map.set(String(c.category.id), c.category)
  }
  for (const t of tracks) {
    for (const c of t.courses || []) {
      if (c.category?.id != null) map.set(String(c.category.id), c.category)
    }
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name))
}

// ---------------------------------------------------------------------------
// Preview dialogs
// ---------------------------------------------------------------------------

function CoursePreviewDialog({ course, open, onClose, onAssign, onUnassign, onOpenLesson, actionLoading, assigned }) {
  if (!course) return null
  const loadingKey = `course-${course.id}`
  const isLoading = actionLoading === loadingKey

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="p-6 pb-3 border-b border-zinc-100">
          <div className="flex items-start gap-3 min-w-0">
            <BookOpen className="h-5 w-5 text-kf-red mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg pr-6 break-words">{course.title}</DialogTitle>
              <DialogDescription asChild>
                <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-zinc-500">
                  {course.category && (
                    <Badge variant="outline" className="text-xs">{course.category.name}</Badge>
                  )}
                  <span>
                    {course.lessonsCount} {course.lessonsCount === 1 ? "lesson" : "lessons"}
                  </span>
                </div>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-w-0">
          {course.description && (
            <p className="text-sm text-zinc-600 whitespace-pre-line break-words">{course.description}</p>
          )}

          <div className="space-y-2 min-w-0">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Lessons</p>
            {course.lessons?.length > 0 ? (
              <ol className="divide-y divide-zinc-100 border border-zinc-200 rounded-md overflow-hidden">
                {course.lessons.map((l, i) => (
                  <li key={l.id}>
                    <button
                      type="button"
                      onClick={() => onOpenLesson(l)}
                      className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-zinc-50 transition-colors min-w-0"
                    >
                      <span className="text-xs text-zinc-400 w-5 text-right shrink-0">{i + 1}</span>
                      <span className="flex-1 text-sm text-zinc-900 truncate min-w-0">{l.title}</span>
                      {l.coachOnly && (
                        <Badge variant="secondary" className="text-xs shrink-0">
                          <EyeOff className="h-3 w-3 mr-1" />Coach only
                        </Badge>
                      )}
                      <ChevronRight className="h-4 w-4 text-zinc-300 shrink-0" />
                    </button>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm text-zinc-400 italic">No lessons yet.</p>
            )}
          </div>

          {course.files?.length > 0 && (
            <div className="space-y-2 min-w-0">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Files</p>
              <ul className="space-y-1.5">
                {course.files.map((f) => (
                  <li key={f.id}>
                    <a
                      href={f.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-kf-red hover:underline min-w-0"
                    >
                      <FileText className="h-3.5 w-3.5 shrink-0 text-zinc-500" />
                      <span className="truncate">{f.title}</span>
                      <Download className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="p-6 pt-3 border-t border-zinc-100 gap-2 sm:gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          {assigned ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => onUnassign(course.id)}
              disabled={isLoading}
              className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Minus className="h-4 w-4 mr-1.5" />}
              Remove
            </Button>
          ) : (
            <Button
              type="button"
              variant="gradient"
              onClick={() => onAssign(course.id)}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Assign
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function TrackPreviewDialog({ track, onClose, onAssign, onUnassign, onOpenCourse, actionLoading, assigned }) {
  if (!track) return null
  const loadingKey = `track-${track.id}`
  const isLoading = actionLoading === loadingKey

  return (
    <Dialog open={!!track} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="p-6 pb-3 border-b border-zinc-100">
          <div className="flex items-start gap-3 min-w-0">
            <Layers className="h-5 w-5 text-kf-red mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg pr-6 break-words">{track.title}</DialogTitle>
              <DialogDescription asChild>
                <span className="text-xs text-zinc-500">
                  {track.coursesCount} {track.coursesCount === 1 ? "course" : "courses"}
                </span>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4 min-w-0">
          {track.description && (
            <p className="text-sm text-zinc-600 whitespace-pre-line break-words">{track.description}</p>
          )}

          <div className="space-y-2 min-w-0">
            <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Courses in this track</p>
            {track.courses?.length > 0 ? (
              <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-md overflow-hidden">
                {track.courses.map((c, i) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => onOpenCourse(c.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors min-w-0"
                    >
                      <span className="text-xs text-zinc-400 w-5 text-right shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">{c.title}</p>
                        <p className="text-xs text-zinc-500 mt-0.5">
                          {c.lessonsCount} {c.lessonsCount === 1 ? "lesson" : "lessons"}
                        </p>
                      </div>
                      {c.category && (
                        <Badge variant="outline" className="text-xs shrink-0 hidden sm:inline-flex">{c.category.name}</Badge>
                      )}
                      <ChevronRight className="h-4 w-4 text-zinc-300 shrink-0" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-zinc-400 italic">No published courses in this track.</p>
            )}
            <p className="text-xs text-zinc-400">Click a course to preview its lessons.</p>
          </div>
        </div>

        <DialogFooter className="p-6 pt-3 border-t border-zinc-100 gap-2 sm:gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          {assigned ? (
            !track.coachLocked && <Button
              type="button"
              variant="outline"
              onClick={() => onUnassign(track.id)}
              disabled={isLoading}
              className="text-red-600 hover:text-red-700 border-red-200 hover:bg-red-50"
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Minus className="h-4 w-4 mr-1.5" />}
              Remove
            </Button>
          ) : (
            <Button
              type="button"
              variant="gradient"
              onClick={() => onAssign(track.id)}
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Plus className="h-4 w-4 mr-1.5" />}
              Assign
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function LessonPreviewDialog({ lesson, courseTitle, onClose, onBack }) {
  if (!lesson) return null

  const resourceIcon = (type) => {
    if (type === "link") return <LinkIcon className="h-3.5 w-3.5" />
    if (type === "video") return <Video className="h-3.5 w-3.5" />
    return <Paperclip className="h-3.5 w-3.5" />
  }

  return (
    <Dialog open={!!lesson} onOpenChange={(o) => { if (!o) onClose() }}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col gap-0 p-0">
        <DialogHeader className="p-6 pb-3 border-b border-zinc-100">
          <div className="flex items-start gap-3 min-w-0">
            <FileText className="h-5 w-5 text-kf-red mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-lg pr-6 break-words">{lesson.title}</DialogTitle>
              <DialogDescription asChild>
                <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-zinc-500">
                  {courseTitle && <span className="truncate">{courseTitle}</span>}
                  {lesson.coachOnly && (
                    <Badge variant="secondary" className="text-xs">
                      <EyeOff className="h-3 w-3 mr-1" />Coach only
                    </Badge>
                  )}
                </div>
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5 min-w-0">
          {lesson.content ? (
            <div className="space-y-2 min-w-0">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Content</p>
              <div
                className="prose prose-sm max-w-none text-zinc-700 break-words whitespace-pre-line"
                dangerouslySetInnerHTML={{ __html: sanitizeContentHtml(lesson.content) }}
              />
            </div>
          ) : (
            !lesson.videoUrl && (
              <p className="text-sm text-zinc-400 italic">No content yet.</p>
            )
          )}

          {lesson.videoUrl && (
            <a
              href={lesson.videoUrl}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-2 px-3 py-2 rounded-md border border-zinc-200 text-sm text-zinc-700 hover:bg-zinc-50 transition-colors min-w-0"
            >
              <Video className="h-4 w-4 text-kf-red shrink-0" />
              <span className="truncate">{lesson.videoUrl}</span>
            </a>
          )}

          {lesson.questions?.length > 0 && (
            <div className="space-y-2 min-w-0">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide flex items-center gap-1.5">
                <HelpCircle className="h-3.5 w-3.5" /> Questions
              </p>
              <p className="text-xs text-zinc-500">
                Preview only. Members answer these on the lesson page after the course is assigned.
              </p>
              <ol className="space-y-3">
                {lesson.questions.map((q, i) => (
                  <li key={q.id} className="flex gap-2 text-sm text-zinc-700 break-words">
                    <span className="text-zinc-400 shrink-0">{i + 1}.</span>
                    <div className="min-w-0 space-y-1.5">
                      <span>{q.text}</span>
                      {q.questionType && q.questionType !== "free_text" ? (
                        <ul className="space-y-1 text-zinc-600">
                          {(q.choices || []).map((c) => (
                            <li key={c.key} className="flex items-center gap-2">
                              <span className={`h-3 w-3 shrink-0 border border-zinc-300 ${q.questionType === "multi_select" ? "rounded-sm" : "rounded-full"}`} />
                              {c.text}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-zinc-400 italic">Written answer</p>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}

          {lesson.resources?.length > 0 && (
            <div className="space-y-2 min-w-0">
              <p className="text-xs font-medium text-zinc-500 uppercase tracking-wide">Resources</p>
              <ul className="space-y-1.5">
                {lesson.resources.map((r) => (
                  <li key={r.id}>
                    {(r.fileUrl || r.url) ? (
                      <a
                        href={r.fileUrl || r.url}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 text-sm text-kf-red hover:underline min-w-0"
                      >
                        <span className="shrink-0 text-zinc-500">{resourceIcon(r.resourceType)}</span>
                        <span className="truncate">{r.title}</span>
                      </a>
                    ) : (
                      <span className="flex items-center gap-2 text-sm text-zinc-700 min-w-0">
                        <span className="shrink-0 text-zinc-500">{resourceIcon(r.resourceType)}</span>
                        <span className="truncate">{r.title}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <DialogFooter className="p-6 pt-3 border-t border-zinc-100 justify-between sm:justify-between gap-2">
          {onBack ? (
            <Button type="button" variant="ghost" onClick={onBack}>
              <ChevronLeft className="h-4 w-4 mr-1" />
              Back to course
            </Button>
          ) : <span />}
          <Button type="button" variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

// Row actions for the All Courses tab. Three states, not two: a course can be
// unassigned, assigned directly, or reachable only because an assigned track
// contains it. The third gets NO unassign — removing one course from a track is
// not an operation the data model has, so the button would silently do nothing.
function CourseAllActions({ course, assignment, isLoading, onAssign, onUnassign }) {
  const state = assignment?.state || "none"

  if (state === "none") {
    return (
      <Button variant="gradient" size="sm" onClick={() => onAssign(course.id)} disabled={isLoading}>
        {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        <span className="ml-1">Assign</span>
      </Button>
    )
  }

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => router.visit(`/app/coach/courses/${course.id}`)}
        className="hidden sm:inline-flex"
      >
        Progress
      </Button>
      {state === "direct" && (
        <RowActionsMenu
          label={`More actions for course ${course.title}`}
          isLoading={isLoading}
          progressHref={`/app/coach/courses/${course.id}`}
          onRemove={() => onUnassign(course.id)}
          removeHint={MENU_HINTS.removeCourse}
        />
      )}
    </>
  )
}

// `variant` drives the row actions:
//   "assigned" — the Assigned tab: progress and date-assigned columns, Archive, Remove.
//   "archived" — the Archived tab (#649): adds the archived date; Restore, Remove.
//   "catalog"  — a list that is unassigned by construction: Assign only.
//   "all"      — the All Courses tab, which mixes both. Each row asks
//                `assignmentFor(id)` how it is assigned and renders accordingly.
function CoursesTable({ courses, variant, onPreview, onAction, onUnassign, onArchive, onRestore, assignmentFor, actionLoading }) {
  // #649 — Assigned opens newest assignment first, Archived newest archive
  // first. Every other list keeps title.
  const defaultSortKey =
    variant === "assigned" ? "assignedAt" : variant === "archived" ? "archivedAt" : "title"
  const defaultDirection = DATE_COLUMNS.has(defaultSortKey) ? "desc" : "asc"
  const [sortKey, setSortKey] = useState(defaultSortKey)
  const [direction, setDirection] = useState(defaultDirection)
  // Progress and date assigned exist only for a course the group has.
  const groupHasCourse = variant === "assigned" || variant === "archived"

  const handleSort = (col) => {
    sortCycleThreeState(
      sortKey, setSortKey, direction, setDirection, col,
      defaultSortKey, defaultDirection, DATE_COLUMNS.has(col) ? "desc" : "asc"
    )
  }

  const sortedCourses = useMemo(() => {
    const arr = [...courses]
    arr.sort((a, b) => {
      if (sortKey === "title") {
        return cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), direction)
      }
      if (sortKey === "category") {
        const r = cmpStr(
          (a.category?.name || "").toLowerCase(),
          (b.category?.name || "").toLowerCase(),
          direction
        )
        return r !== 0 ? r : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      if (sortKey === "lessons") {
        const r = cmpNum(a.lessonsCount || 0, b.lessonsCount || 0, direction)
        return r !== 0
          ? r
          : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      if (sortKey === "progress" && groupHasCourse) {
        const ra = courseProgressRatio(a)
        const rb = courseProgressRatio(b)
        const r = cmpNum(ra, rb, direction)
        return r !== 0 ? r : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      if (DATE_COLUMNS.has(sortKey)) {
        const r = cmpDate(a[sortKey], b[sortKey], direction)
        return r !== 0 ? r : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      return 0
    })
    return arr
  }, [courses, sortKey, direction, groupHasCourse])

  return (
    <div className="border border-zinc-200 rounded-md overflow-x-auto overscroll-x-contain">
      <Table className="min-w-[44rem] w-full">
        <TableHeader>
          <TableRow>
            <SortableHeader
              column="title"
              sortKey={sortKey}
              direction={direction}
              onSort={handleSort}
              className="min-w-[12rem]"
            >
              Course
            </SortableHeader>
            <SortableHeader
              column="category"
              sortKey={sortKey}
              direction={direction}
              onSort={handleSort}
              className="hidden md:table-cell w-[11rem] min-w-[9rem] align-top"
            >
              Category
            </SortableHeader>
            <SortableHeader
              column="lessons"
              sortKey={sortKey}
              direction={direction}
              onSort={handleSort}
              className="hidden sm:table-cell w-20 text-right whitespace-nowrap"
            >
              Lessons
            </SortableHeader>
            {groupHasCourse && (
              <SortableHeader
                column="progress"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
                className="hidden lg:table-cell w-44"
              >
                Progress
              </SortableHeader>
            )}
            {groupHasCourse && (
              <SortableHeader
                column="assignedAt"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
                className="w-32 whitespace-nowrap"
              >
                Assigned
              </SortableHeader>
            )}
            {variant === "archived" && (
              <SortableHeader
                column="archivedAt"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
                className="w-32 whitespace-nowrap"
              >
                Archived
              </SortableHeader>
            )}
            <TableHead className={variant === "catalog" ? "w-28 text-right" : "w-40 text-right"}>
              Actions
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sortedCourses.map((course) => {
            const loadingKey = `course-${course.id}`
            const isLoading = actionLoading === loadingKey
            const assignment =
              variant === "all" ? assignmentFor?.(course.id) : null
            return (
              <TableRow
                key={course.id}
                className="cursor-pointer"
                onClick={() => onPreview(course)}
              >
                <TableCell className="whitespace-normal">
                  <div className="flex items-center gap-3 min-w-0">
                    <CourseCoverImage
                      url={course.coverImageUrl}
                      alt=""
                      wrapperClassName="w-10 h-10 rounded shrink-0 overflow-hidden"
                    />
                    <div className="min-w-0">
                      <p className="font-medium text-zinc-900 truncate">
                        {course.title}
                        {variant === "archived" && <ArchivedBadge />}
                      </p>
                      {assignment && assignment.state !== "none" && (
                        <p className="text-xs text-zinc-500 mt-0.5 truncate">
                          {assignment.state === "track"
                            ? `In track · ${assignment.trackTitle}`
                            : "Assigned"}
                        </p>
                      )}
                      <p className="text-xs text-zinc-500 mt-0.5 md:hidden truncate">
                        {course.category?.name || ""}
                        {course.category?.name && " · "}
                        {course.lessonsCount} {course.lessonsCount === 1 ? "lesson" : "lessons"}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="hidden md:table-cell align-top py-3">
                  {course.category ? (
                    <Badge
                      variant="outline"
                      className="text-xs font-normal max-w-[14rem] !whitespace-normal text-left leading-snug h-auto min-h-7 py-1.5 !items-start shrink !overflow-visible"
                    >
                      {course.category.name}
                    </Badge>
                  ) : (
                    <span className="text-xs text-zinc-400">—</span>
                  )}
                </TableCell>
                <TableCell className="hidden sm:table-cell text-right text-sm text-zinc-600">
                  {course.lessonsCount}
                </TableCell>
                {groupHasCourse && (
                  <TableCell className="hidden lg:table-cell">
                    {course.progress ? (
                      <div className="space-y-1">
                        <span className="text-xs text-zinc-600 flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {course.progress.completedMembers}/{course.progress.totalMembers}
                        </span>
                        <div className="h-1.5 bg-zinc-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-kf-red rounded-full transition-all"
                            style={{
                              width: course.progress.totalMembers > 0
                                ? `${(course.progress.completedMembers / course.progress.totalMembers) * 100}%`
                                : "0%"
                            }}
                          />
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-zinc-400">—</span>
                    )}
                  </TableCell>
                )}
                {groupHasCourse && (
                  <TableCell>
                    <DateCell iso={course.assignedAt} />
                  </TableCell>
                )}
                {variant === "archived" && (
                  <TableCell>
                    <DateCell iso={course.archivedAt} />
                  </TableCell>
                )}
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex justify-end gap-1">
                    {variant === "all" ? (
                      <CourseAllActions
                        course={course}
                        assignment={assignment}
                        isLoading={isLoading}
                        onAssign={onAction}
                        onUnassign={onUnassign}
                      />
                    ) : variant === "catalog" ? (
                      <Button
                        variant="gradient"
                        size="sm"
                        onClick={() => onAction(course.id)}
                        disabled={isLoading}
                      >
                        {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        <span className="ml-1">Assign</span>
                      </Button>
                    ) : (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => router.visit(`/app/coach/courses/${course.id}`)}
                          className="hidden sm:inline-flex"
                        >
                          Progress
                        </Button>
                        <RowActionsMenu
                          label={`More actions for course ${course.title}`}
                          isLoading={isLoading}
                          progressHref={`/app/coach/courses/${course.id}`}
                          onArchive={variant === "assigned" && onArchive ? () => onArchive(course.id) : null}
                          onRestore={variant === "archived" && onRestore ? () => onRestore(course.id) : null}
                          onRemove={() => onAction(course.id)}
                          removeHint={MENU_HINTS.removeCourse}
                        />
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </div>
  )
}

// Three counts, not one ratio. Production tracks run 10-22 courses, so
// whole-track completion is rare and late -- "1 of 10 complete" hides the four
// people who are actually moving through it.
function TrackProgressCell({ progress }) {
  if (!progress || progress.totalMembers === 0) {
    return <span className="text-xs text-zinc-400 italic">No members</span>
  }

  const pct = (n) => `${(100 * n) / progress.totalMembers}%`

  return (
    <div className="min-w-[10rem]">
      <div className="flex h-2 rounded-full overflow-hidden bg-zinc-100">
        <div className="bg-emerald-500" style={{ width: pct(progress.complete) }} />
        <div className="bg-amber-400" style={{ width: pct(progress.inProgress) }} />
      </div>
      <div className="flex flex-wrap gap-x-3 mt-1 text-[11px] text-zinc-500 tabular-nums">
        <span>
          <span className="inline-block h-2 w-2 rounded-sm bg-emerald-500 mr-1" />
          {progress.complete} complete
        </span>
        <span>
          <span className="inline-block h-2 w-2 rounded-sm bg-amber-400 mr-1" />
          {progress.inProgress} in progress
        </span>
        <span>
          <span className="inline-block h-2 w-2 rounded-sm bg-zinc-200 mr-1" />
          {progress.notStarted} not started
        </span>
      </div>
    </div>
  )
}

function TracksTable({ tracks, variant, onPreview, onAssign, onUnassign, onReorder, onArchive, onRestore, isAssigned, actionLoading, coachGroup }) {
  const [confirmTrack, setConfirmTrack] = useState(null)
  // #547 -- the assigned tab defaults to the coach's stored order, not title.
  // Catalog and All Tracks keep "title": a track not assigned to this group has
  // no custom order to fall back to. #649 -- Archived opens newest archive first.
  const defaultSortKey =
    variant === "assigned" ? "custom" : variant === "archived" ? "archivedAt" : "title"
  const defaultDirection = DATE_COLUMNS.has(defaultSortKey) ? "desc" : "asc"
  const [sortKey, setSortKey] = useState(defaultSortKey)
  const [direction, setDirection] = useState(defaultDirection)

  const handleSort = (col) => {
    sortCycleThreeState(
      sortKey, setSortKey, direction, setDirection, col,
      defaultSortKey, defaultDirection, DATE_COLUMNS.has(col) ? "desc" : "asc"
    )
  }

  // Only an assigned track has a group behind it, so only those carry progress.
  const showProgress = tracks.some((t) => t.progress)

  // #547 -- the order column only makes sense while the table is in the coach's
  // custom order. Under a title sort the number and the arrows would both lie.
  const showOrder = !!onReorder && sortKey === "custom" && variant === "assigned"

  const sortedTracks = useMemo(() => {
    // Server order IS the order. No comparator -- see #547.
    if (sortKey === "custom") return tracks
    const arr = [...tracks]
    arr.sort((a, b) => {
      if (sortKey === "title") {
        return cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), direction)
      }
      if (sortKey === "total") {
        const r = cmpNum(a.coursesCount || 0, b.coursesCount || 0, direction)
        return r !== 0
          ? r
          : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      if (sortKey === "progress") {
        const share = (t) =>
          t.progress?.totalMembers > 0 ? t.progress.complete / t.progress.totalMembers : -1
        const r = cmpNum(share(a), share(b), direction)
        return r !== 0
          ? r
          : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      if (sortKey === "archivedAt") {
        const r = cmpDate(a.archivedAt, b.archivedAt, direction)
        return r !== 0
          ? r
          : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      return 0
    })
    return arr
  }, [tracks, sortKey, direction])

  return (
    <>
      <div className="border border-zinc-200 rounded-md overflow-x-auto overscroll-x-contain">
        <Table className="min-w-[36rem] w-full">
          <TableHeader>
            <TableRow>
              {showOrder && <TableHead className="w-24 pl-3">Order</TableHead>}
              <SortableHeader
                column="title"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
                className="min-w-[10rem]"
              >
                Track
              </SortableHeader>
              <TableHead className="hidden md:table-cell min-w-[12rem]">Courses</TableHead>
              <SortableHeader
                column="total"
                sortKey={sortKey}
                direction={direction}
                onSort={handleSort}
                className="hidden sm:table-cell w-20 text-right"
              >
                Total
              </SortableHeader>
              {showProgress && (
                <SortableHeader
                  column="progress"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                  className="min-w-[11rem]"
                >
                  Progress
                </SortableHeader>
              )}
              {variant === "archived" && (
                <SortableHeader
                  column="archivedAt"
                  sortKey={sortKey}
                  direction={direction}
                  onSort={handleSort}
                  className="w-32 whitespace-nowrap"
                >
                  Archived
                </SortableHeader>
              )}
              <TableHead className={variant === "catalog" ? "w-36 text-right" : "w-40 text-right"}>
                {variant === "catalog" ? "Assign" : "Actions"}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedTracks.map((track, rowIndex) => {
              const loadingKey = `track-${track.id}`
              const isLoading = actionLoading === loadingKey
              // On the All Tracks tab a row can be either state, so ask per row.
              const rowAssigned =
                variant === "all" ? !!isAssigned?.(track.id) : variant === "assigned" || variant === "archived"
              const preview = (track.courseNames || []).slice(0, 2).join(" · ")
              const extra = (track.coursesCount || 0) - 2
              return (
                <TableRow
                  key={track.id}
                  className="cursor-pointer"
                  onClick={() => onPreview(track)}
                >
                  {showOrder && (
                    <TableCell className="pl-3 align-middle" onClick={(e) => e.stopPropagation()}>
                      {rowAssigned ? (
                        <div className="flex items-center gap-1">
                          <span className="text-sm tabular-nums text-zinc-500 w-4">{rowIndex + 1}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="px-1.5"
                            disabled={isLoading || rowIndex === 0}
                            onClick={() => onReorder(track.id, "up")}
                            title="Move up"
                            aria-label={`Move track ${track.title} up`}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            className="px-1.5"
                            disabled={isLoading || rowIndex === sortedTracks.length - 1}
                            onClick={() => onReorder(track.id, "down")}
                            title="Move down"
                            aria-label={`Move track ${track.title} down`}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-zinc-400 italic">—</span>
                      )}
                    </TableCell>
                  )}
                  <TableCell className="whitespace-normal">
                    <div className="flex items-start gap-3 min-w-0">
                      <Layers className="h-4 w-4 text-kf-red shrink-0 mt-1" />
                      <div className="min-w-0">
                        <p className="font-medium text-zinc-900 truncate">
                          {track.title}
                          {variant === "archived" && <ArchivedBadge />}
                        </p>
                        {variant === "all" && rowAssigned && (
                          <p className="text-xs text-zinc-500 mt-0.5">Assigned</p>
                        )}
                        {track.description && (
                          <p className="text-xs text-zinc-500 line-clamp-1 mt-0.5">{track.description}</p>
                        )}
                        <p className="text-xs text-zinc-500 mt-0.5 sm:hidden">
                          {track.coursesCount} {track.coursesCount === 1 ? "course" : "courses"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-zinc-500">
                    {preview || <span className="text-zinc-400 italic">—</span>}
                    {extra > 0 && <span className="text-zinc-400"> +{extra} more</span>}
                  </TableCell>
                  <TableCell className="hidden sm:table-cell text-right text-sm text-zinc-600">
                    {track.coursesCount}
                  </TableCell>
                  {showProgress && (
                    <TableCell className="whitespace-normal">
                      {track.progress ? (
                        <TrackProgressCell progress={track.progress} />
                      ) : (
                        <span className="text-xs text-zinc-400 italic">—</span>
                      )}
                    </TableCell>
                  )}
                  {variant === "archived" && (
                    <TableCell>
                      <DateCell iso={track.archivedAt} />
                    </TableCell>
                  )}
                  <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex justify-end gap-1">
                      {rowAssigned && (
                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="hidden sm:inline-flex"
                        >
                          <Link href={`/app/coach/learning_tracks/${track.id}`}>Progress</Link>
                        </Button>
                      )}
                      {!rowAssigned ? (
                        <Button
                          variant="gradient"
                          size="sm"
                          onClick={() => onAssign(track.id)}
                          disabled={isLoading}
                        >
                          {isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                          <span className="ml-1">Assign</span>
                        </Button>
                      ) : track.coachLocked ? null : (
                        <>
                          <RowActionsMenu
                            label={`More actions for track ${track.title}`}
                            isLoading={isLoading}
                            progressHref={`/app/coach/learning_tracks/${track.id}`}
                            onArchive={variant === "assigned" && onArchive ? () => onArchive(track.id) : null}
                            onRestore={variant === "archived" && onRestore ? () => onRestore(track.id) : null}
                            onRemove={() => setConfirmTrack(track)}
                            removeHint={`${MENU_HINTS.removeTrack} You confirm first.`}
                          />
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <AlertDialog open={!!confirmTrack} onOpenChange={(o) => { if (!o) setConfirmTrack(null) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove track from group?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes{" "}
              <span className="font-medium text-zinc-700">{confirmTrack?.title}</span>{" "}
              from{" "}
              <span className="font-medium text-zinc-700">{coachGroup?.name}</span>{" "}
              along with{" "}
              {confirmTrack?.coursesCount === 1
                ? "its course"
                : `all ${confirmTrack?.coursesCount} courses`}
              {" "}
              currently assigned as part of this track. The track remains in Catalog—you can assign it again later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => {
                const id = confirmTrack?.id
                setConfirmTrack(null)
                if (id) onUnassign(id)
              }}
            >
              Remove Track
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}

function EmptyState({ icon: Icon = BookOpen, title, description }) {
  return (
    <div className="text-center py-12 text-zinc-500">
      <Icon className="h-12 w-12 mx-auto mb-3 text-zinc-300" />
      <p className="font-medium">{title}</p>
      <p className="text-sm">{description}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// My Courses table — coach-authored courses, any state. Shows the workflow
// status (draft / pending review / public / rejected) so the coach can see
// where each course stands without leaving Content.
// ---------------------------------------------------------------------------

function PublicStatusBadge({ status, course }) {
  if (course.status === "draft") {
    return <Badge variant="secondary" className="bg-zinc-100 text-zinc-700">Draft</Badge>
  }
  if (status === "pending_review") {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100">
        <Clock className="h-3 w-3 mr-1" /> Pending review
      </Badge>
    )
  }
  if (status === "public") {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 hover:bg-emerald-100">
        <Globe className="h-3 w-3 mr-1" /> Public
      </Badge>
    )
  }
  if (status === "rejected") {
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200 hover:bg-red-100">
        <XCircle className="h-3 w-3 mr-1" /> Rejected
      </Badge>
    )
  }
  // public_status === "private" on a published course → just published to my groups
  return <Badge variant="outline" className="border-zinc-300 text-zinc-700">Published · My groups</Badge>
}

function MyCoursesTable({ courses }) {
  const [sortKey, setSortKey] = useState("title")
  const [direction, setDirection] = useState("asc")

  const handleSort = (col) => {
    sortCycleThreeState(sortKey, setSortKey, direction, setDirection, col, "title")
  }

  const sorted = useMemo(() => {
    const arr = [...courses]
    arr.sort((a, b) => {
      if (sortKey === "title") {
        return cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), direction)
      }
      if (sortKey === "category") {
        const r = cmpStr(
          (a.category?.name || "").toLowerCase(),
          (b.category?.name || "").toLowerCase(),
          direction
        )
        return r !== 0 ? r : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      if (sortKey === "lessons") {
        const r = cmpNum(a.lessonsCount || 0, b.lessonsCount || 0, direction)
        return r !== 0 ? r : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      if (sortKey === "status") {
        const r = cmpNum(myCourseStatusOrdinal(a), myCourseStatusOrdinal(b), direction)
        return r !== 0 ? r : cmpStr((a.title || "").toLowerCase(), (b.title || "").toLowerCase(), "asc")
      }
      return 0
    })
    return arr
  }, [courses, sortKey, direction])

  return (
    <div className="rounded-md border border-zinc-200 overflow-x-auto overscroll-x-contain">
      <Table className="min-w-[40rem] w-full">
        <TableHeader>
          <TableRow>
            <SortableHeader
              column="title"
              sortKey={sortKey}
              direction={direction}
              onSort={handleSort}
              className="min-w-[12rem]"
            >
              Title
            </SortableHeader>
            <SortableHeader
              column="category"
              sortKey={sortKey}
              direction={direction}
              onSort={handleSort}
              className="hidden md:table-cell w-[11rem] min-w-[9rem]"
            >
              Category
            </SortableHeader>
            <SortableHeader
              column="lessons"
              sortKey={sortKey}
              direction={direction}
              onSort={handleSort}
              className="hidden sm:table-cell w-[80px] text-center"
            >
              Lessons
            </SortableHeader>
            <SortableHeader
              column="status"
              sortKey={sortKey}
              direction={direction}
              onSort={handleSort}
              className="w-[200px] min-w-[140px]"
            >
              Status
            </SortableHeader>
            <TableHead className="w-[140px] min-w-[120px] text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sorted.map((course) => (
            <TableRow key={course.id}>
              <TableCell className="align-top">
                <div className="min-w-0">
                  <p className="font-medium text-zinc-900">{course.title}</p>
                  <p className="text-xs text-zinc-500 mt-1 md:hidden">
                    {course.category?.name || "—"}
                    {" · "}
                    {course.lessonsCount} {course.lessonsCount === 1 ? "lesson" : "lessons"}
                  </p>
                </div>
              </TableCell>
              <TableCell className="hidden md:table-cell text-zinc-600 align-top">
                <span className="text-sm break-words max-w-[14rem] inline-block">
                  {course.category?.name || "—"}
                </span>
              </TableCell>
              <TableCell className="hidden sm:table-cell text-center text-zinc-600 align-top">
                {course.lessonsCount}
              </TableCell>
              <TableCell>
                <PublicStatusBadge status={course.publicStatus} course={course} />
              </TableCell>
              <TableCell className="text-right">
                <div className="flex justify-end gap-1">
                  <Link href={`/app/coach/courses/${course.id}`}>
                    <Button size="sm" variant="ghost">
                      <Eye className="h-4 w-4 mr-1" /> View
                    </Button>
                  </Link>
                  <Link href={`/app/coach/courses/${course.id}/edit`}>
                    <Button size="sm" variant="ghost">
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  </Link>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

const CONTENT_TAB_KEYS = new Set(["assigned", "archived", "tracks", "courses", "mine"])

// No `?tab=` means All Courses — the coach's default landing (task #519).
function contentTabFromUrl(fullUrl) {
  const t = inertiaUrlTab(fullUrl)
  return t && CONTENT_TAB_KEYS.has(t) ? t : "courses"
}

// ---------------------------------------------------------------------------
// Main
// ---------------------------------------------------------------------------

export default function ContentIndex({
  currentUser,
  assignedCourses = [],
  catalogCourses = [],
  assignedTracks = [],
  catalogTracks = [],
  archivedCourses = [],
  archivedTracks = [],
  myCourses = [],
  coachGroup,
  coachGroups,
}) {
  const { url } = usePage()
  const [selectedTab, setSelectedTab] = useState(() => contentTabFromUrl(url))
  // #649 — the Archived tab exists only while the group has archived content.
  // Restoring the last item drops the coach back on Assigned.
  const archivedCount = archivedCourses.length + archivedTracks.length
  const activeTab = selectedTab === "archived" && archivedCount === 0 ? "assigned" : selectedTab

  useEffect(() => {
    setSelectedTab(contentTabFromUrl(url))
  }, [url])

  const goToTab = (key) => {
    if (!CONTENT_TAB_KEYS.has(key)) return
    setSelectedTab(key)
    router.get("/app/coach/content", { tab: key }, { preserveState: true, preserveScroll: true, replace: true })
  }
  const [searchQuery, setSearchQuery] = useState("")
  const [categoryId, setCategoryId] = useState("")
  const [myCourseStatusFilter, setMyCourseStatusFilter] = useState("all")
  const [actionLoading, setActionLoading] = useState(null)
  const [previewCourse, setPreviewCourse] = useState(null)
  const [previewTrack, setPreviewTrack] = useState(null)
  const [previewLesson, setPreviewLesson] = useState(null)

  const assignCourse = (id) => {
    setActionLoading(`course-${id}`)
    router.post(
      `/app/coach/courses/${id}/assign?return_to=content`,
      {},
      {
        onFinish: () => setActionLoading(null),
        onSuccess: () => setPreviewCourse(null),
      }
    )
  }
  const unassignCourse = (id) => {
    setActionLoading(`course-${id}`)
    router.delete(`/app/coach/courses/${id}/unassign?return_to=content`, {
      onFinish: () => setActionLoading(null),
      onSuccess: () => setPreviewCourse(null),
    })
  }
  const assignTrack = (id) => {
    setActionLoading(`track-${id}`)
    router.post(`/app/coach/learning_tracks/${id}/assign`, {}, {
      onFinish: () => setActionLoading(null),
      onSuccess: () => setPreviewTrack(null),
    })
  }
  const unassignTrack = (id) => {
    setActionLoading(`track-${id}`)
    router.delete(`/app/coach/learning_tracks/${id}/unassign`, {
      onFinish: () => setActionLoading(null),
      onSuccess: () => setPreviewTrack(null),
    })
  }
  // #649 — archive and restore are POSTs that redirect back with a notice, like
  // assign. The server owns the new lists; no optimistic state here either.
  const archivePost = (kind, id, action) => {
    setActionLoading(`${kind}-${id}`)
    const path =
      kind === "course"
        ? `/app/coach/courses/${id}/${action}?return_to=content`
        : `/app/coach/learning_tracks/${id}/${action}`
    router.post(path, {}, {
      preserveScroll: true,
      onFinish: () => setActionLoading(null),
    })
  }
  const archiveCourse = (id) => archivePost("course", id, "archive")
  const restoreCourse = (id) => archivePost("course", id, "unarchive")
  const archiveTrack = (id) => archivePost("track", id, "archive")
  const restoreTrack = (id) => archivePost("track", id, "unarchive")

  // #547 -- no optimistic state. Guessing the new order here would be a second
  // implementation of the renumber rule on the client, which is exactly what the
  // shared server-side scope exists to prevent.
  const reorderTrack = (id, direction) => {
    setActionLoading(`track-${id}`)
    router.patch(`/app/coach/learning_tracks/${id}/reorder`, { direction }, {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => setActionLoading(null),
    })
  }

  const searching = !!searchQuery.trim()

  // A category picked from the search-mode union list is not necessarily valid
  // for the tab she lands back on, so clear it on either mode change.
  useEffect(() => {
    setCategoryId("")
  }, [activeTab, searching])

  const trackMatchesCategory = (t) => {
    if (!categoryId) return true
    return t.courses?.some((c) => String(c.category?.id) === categoryId)
  }

  const courseMatchesCategory = (c) =>
    !categoryId || String(c.category?.id) === categoryId

  // The All tabs (tasks #520/#521) are the union of what the controller already
  // ships — no extra props and no bigger payload, because every course in the
  // union is serialized in the response today. Reading assignment state from
  // the same arrays that build the list is also what keeps the two in step.
  //
  // `catalogCourses` deliberately excludes courses reachable through an
  // assigned track, so the track courses have to be folded back in here.
  const allCourses = useMemo(() => {
    const byId = new Map()
    for (const c of catalogCourses) byId.set(c.id, c)
    for (const t of assignedTracks) {
      for (const c of t.courses || []) if (!byId.has(c.id)) byId.set(c.id, c)
    }
    // Assigned last: those carry the progress payload, so they win on conflict.
    for (const c of assignedCourses) byId.set(c.id, c)
    return [...byId.values()].sort((a, b) =>
      (a.title || "").localeCompare(b.title || "")
    )
  }, [catalogCourses, assignedCourses, assignedTracks])

  const allTracks = useMemo(() => {
    const byId = new Map()
    for (const t of catalogTracks) byId.set(t.id, t)
    for (const t of assignedTracks) byId.set(t.id, t)
    return [...byId.values()].sort((a, b) =>
      (a.title || "").localeCompare(b.title || "")
    )
  }, [catalogTracks, assignedTracks])

  // How a course reaches this group — "direct" (a group_course), "track" (it
  // sits inside an assigned track) or "none". A track course is deliberately
  // NOT individually removable; see CourseAllActions.
  const courseAssignment = useMemo(() => {
    const map = new Map()
    for (const t of assignedTracks) {
      for (const c of t.courses || []) {
        if (!map.has(c.id)) map.set(c.id, { state: "track", trackTitle: t.title })
      }
    }
    for (const c of assignedCourses) map.set(c.id, { state: "direct" })
    return (id) => map.get(id) || { state: "none" }
  }, [assignedCourses, assignedTracks])

  const filteredAssignedCourses = useMemo(
    () =>
      assignedCourses.filter(
        (c) => matchesCourse(c, searchQuery) && courseMatchesCategory(c)
      ),
    [assignedCourses, searchQuery, categoryId]
  )

  const filteredAssignedTracks = useMemo(
    () =>
      assignedTracks.filter((t) => matchesTrack(t, searchQuery) && trackMatchesCategory(t)),
    [assignedTracks, searchQuery, categoryId]
  )

  const filteredAllCourses = useMemo(
    () =>
      allCourses.filter(
        (c) => matchesCourse(c, searchQuery) && courseMatchesCategory(c)
      ),
    [allCourses, searchQuery, categoryId]
  )

  const filteredAllTracks = useMemo(
    () =>
      allTracks.filter((t) => matchesTrack(t, searchQuery) && trackMatchesCategory(t)),
    [allTracks, searchQuery, categoryId]
  )

  const filteredMyCourses = useMemo(() => {
    return myCourses.filter((c) => {
      if (!matchesCourse(c, searchQuery)) return false
      if (!courseMatchesCategory(c)) return false
      if (myCourseStatusFilter !== "all" && myCourseWorkflowKey(c) !== myCourseStatusFilter) {
        return false
      }
      return true
    })
  }, [myCourses, searchQuery, categoryId, myCourseStatusFilter])

  const filteredArchivedCourses = useMemo(
    () =>
      archivedCourses.filter(
        (c) => matchesCourse(c, searchQuery) && courseMatchesCategory(c)
      ),
    [archivedCourses, searchQuery, categoryId]
  )

  const filteredArchivedTracks = useMemo(
    () =>
      archivedTracks.filter((t) => matchesTrack(t, searchQuery) && trackMatchesCategory(t)),
    [archivedTracks, searchQuery, categoryId]
  )

  const tabCategories = useMemo(() => {
    if (activeTab === "assigned") {
      return categoriesFromCoursesAndTracks(assignedCourses, assignedTracks)
    }
    if (activeTab === "archived") {
      return categoriesFromCoursesAndTracks(archivedCourses, archivedTracks)
    }
    if (activeTab === "tracks") {
      return categoriesFromCoursesAndTracks([], allTracks)
    }
    if (activeTab === "courses") {
      return uniqueCategoriesFromCourses(allCourses)
    }
    return uniqueCategoriesFromCourses(myCourses)
  }, [activeTab, assignedCourses, assignedTracks, archivedCourses, archivedTracks, allTracks, allCourses, myCourses])

  const categoryOptions = useMemo(
    () =>
      searching
        ? allCategories({
            assignedCourses,
            assignedTracks,
            archivedCourses,
            archivedTracks,
            catalogTracks,
            catalogCourses,
            myCourses,
          })
        : tabCategories,
    [
      searching,
      tabCategories,
      assignedCourses,
      assignedTracks,
      archivedCourses,
      archivedTracks,
      catalogTracks,
      catalogCourses,
      myCourses,
    ]
  )

  // 3a: computed unconditionally; only the render is gated on `searching`.
  const searchGroups = useMemo(
    () =>
      buildSearchGroups(
        {
          assignedCourses,
          assignedTracks,
          archivedCourses,
          archivedTracks,
          catalogTracks,
          catalogCourses,
          myCourses,
        },
        { query: searchQuery, categoryId, myCourseStatusFilter }
      ),
    [
      assignedCourses,
      assignedTracks,
      archivedCourses,
      archivedTracks,
      catalogTracks,
      catalogCourses,
      myCourses,
      searchQuery,
      categoryId,
      myCourseStatusFilter,
    ]
  )

  const searchTotal = searchGroups.reduce((sum, g) => sum + g.count, 0)

  const filtersActive =
    !!searchQuery.trim() ||
    !!categoryId ||
    (activeTab === "mine" && myCourseStatusFilter !== "all")

  const resetFilters = () => {
    setSearchQuery("")
    setCategoryId("")
    setMyCourseStatusFilter("all")
  }

  const assignedCount = assignedCourses.length + assignedTracks.length
  // Archived content is still the group's: its preview offers Remove, never an
  // Assign that would silently do nothing (#649).
  const courseIsAssigned = (id) =>
    assignedCourses.some((c) => c.id === id) ||
    archivedCourses.some((c) => c.id === id) ||
    [...assignedTracks, ...archivedTracks].some((t) => t.courses?.some((c) => c.id === id))
  const trackIsAssigned = (id) =>
    assignedTracks.some((t) => t.id === id) || archivedTracks.some((t) => t.id === id)

  // When drilling from a track preview into one of its courses, find the
  // course in either the catalog or the track's own course list, then load
  // the full course (with lessons) from catalogCourses/assignedCourses if
  // available.
  const openCourseFromTrack = (courseId) => {
    const toOpen =
      previewTrack?.courses?.find((c) => c.id === courseId) ||
      assignedCourses.find((c) => c.id === courseId) ||
      archivedCourses.find((c) => c.id === courseId) ||
      catalogCourses.find((c) => c.id === courseId)
    if (!toOpen) return
    setPreviewTrack(null)
    setPreviewCourse(toOpen)
  }

  const tabs = [
    { key: "assigned", label: "Assigned", count: assignedCount },
    ...(archivedCount > 0 ? [{ key: "archived", label: "Archived", count: archivedCount }] : []),
    { key: "tracks", label: "All Tracks", count: allTracks.length },
    { key: "courses", label: "All Courses", count: allCourses.length },
    { key: "mine", label: "My Courses", count: myCourses.length },
  ]

  const assignedHasTracks = assignedTracks.length > 0
  const assignedHasCourses = assignedCourses.length > 0

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title="Content - Kingdom Factor" />

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">Content</h1>
            <p className="text-zinc-600">
              Manage courses and tracks for{" "}
              {coachGroups?.length >= 1 ? (
                <CoachGroupSwitcher coachGroup={coachGroup} coachGroups={coachGroups} />
              ) : (
                <span className="font-medium">{coachGroup?.name}</span>
              )}
            </p>
          </div>
          <Link href="/app/coach/courses/new">
            <Button variant="gradient">
              <Plus className="h-4 w-4 mr-1.5" />
              Create Course
            </Button>
          </Link>
        </div>

        <div className="flex gap-1 border-b border-zinc-200 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => goToTab(tab.key)}
              className={cn(
                "px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                activeTab === tab.key
                  ? "border-kf-red text-kf-red"
                  : "border-transparent text-zinc-500 hover:text-zinc-700"
              )}
            >
              {tab.label}
              <span className="ml-1.5 text-xs text-zinc-400">({tab.count})</span>
            </button>
          ))}
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-4 shadow-xs space-y-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:gap-4">
            <div className="relative flex-1 min-w-0 xl:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 pointer-events-none" />
              <Input
                type="search"
                placeholder="Search all courses and tracks…"
                className="pl-9 w-full"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="flex flex-col gap-2 min-w-0 sm:flex-row sm:flex-wrap sm:items-center flex-1">
              {categoryOptions.length > 0 && (
                <Select
                  value={categoryId || "all"}
                  onValueChange={(v) => setCategoryId(v === "all" ? "" : v)}
                >
                  <SelectTrigger
                    className="w-full sm:w-[12.5rem] h-9 shrink-0"
                    aria-label="Filter by category"
                  >
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categoryOptions.map((cat) => (
                      <SelectItem key={cat.id} value={String(cat.id)}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              {!searching && activeTab === "mine" && (
                <Select value={myCourseStatusFilter} onValueChange={setMyCourseStatusFilter}>
                  <SelectTrigger
                    className="w-full sm:w-[12.5rem] h-9 shrink-0"
                    aria-label="Filter by publish status"
                  >
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="pending_review">Pending review</SelectItem>
                    <SelectItem value="public">Public catalog</SelectItem>
                    <SelectItem value="private">Published · My groups</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 pt-3">
            <div className="flex items-center gap-2 text-xs text-zinc-500 min-w-0">
              <Filter className="h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 leading-relaxed" aria-live="polite">
                {searching &&
                  (searchTotal === 0
                    ? "No matches"
                    : `${searchTotal} ${searchTotal === 1 ? "match" : "matches"} across ${searchGroups
                        .map((g) => g.label)
                        .join(" · ")}`)}
                {!searching && activeTab === "tracks" &&
                  `${filteredAllTracks.length} of ${allTracks.length} tracks`}
                {!searching && activeTab === "courses" &&
                  `${filteredAllCourses.length} of ${allCourses.length} courses`}
                {!searching && activeTab === "mine" &&
                  `${filteredMyCourses.length} of ${myCourses.length} your courses`}
                {!searching && activeTab === "assigned" && (
                  <>
                    {assignedHasTracks &&
                      `${filteredAssignedTracks.length} of ${assignedTracks.length} tracks`}
                    {assignedHasTracks && assignedHasCourses && " · "}
                    {assignedHasCourses &&
                      `${filteredAssignedCourses.length} of ${assignedCourses.length} courses`}
                  </>
                )}
                {!searching && activeTab === "archived" && (
                  <>
                    {archivedTracks.length > 0 &&
                      `${filteredArchivedTracks.length} of ${archivedTracks.length} tracks`}
                    {archivedTracks.length > 0 && archivedCourses.length > 0 && " · "}
                    {archivedCourses.length > 0 &&
                      `${filteredArchivedCourses.length} of ${archivedCourses.length} courses`}
                  </>
                )}
              </span>
            </div>
            {filtersActive && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 text-zinc-600 shrink-0"
                onClick={resetFilters}
              >
                <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                Reset filters
              </Button>
            )}
          </div>
        </div>

        {searching && searchGroups.length === 0 && (
          <EmptyState
            title="No matches"
            description={`No content matches \u201c${searchQuery.trim()}\u201d.`}
          />
        )}

        {searching && searchGroups.length > 0 && (
          <div className="space-y-8">
            {searchGroups.map((group) => (
              <div key={group.key} className="space-y-3">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                      {group.kind === "tracks" ? (
                        <Layers className="h-5 w-5 text-kf-red" />
                      ) : (
                        <BookOpen className="h-5 w-5 text-kf-red" />
                      )}
                      {group.label} ({group.count})
                    </h2>
                    {group.key === "myCourses" && (
                      <p className="text-xs text-zinc-500 mt-1">
                        Courses you authored — one may also appear under Assigned.
                      </p>
                    )}
                  </div>
                  {group.key === "myCourses" && (
                    <Select value={myCourseStatusFilter} onValueChange={setMyCourseStatusFilter}>
                      <SelectTrigger
                        className="w-full sm:w-[12.5rem] h-9 shrink-0"
                        aria-label="Filter My Courses by publish status"
                      >
                        <SelectValue placeholder="Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="pending_review">Pending review</SelectItem>
                        <SelectItem value="public">Public catalog</SelectItem>
                        <SelectItem value="private">Published · My groups</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {group.key === "assignedTracks" && (
                  <TracksTable
                    tracks={group.items}
                    variant="assigned"
                    coachGroup={coachGroup}
                    onPreview={setPreviewTrack}
                    onUnassign={unassignTrack}
                    onReorder={reorderTrack}
                    onArchive={archiveTrack}
                    actionLoading={actionLoading}
                  />
                )}
                {group.key === "assignedCourses" && (
                  <CoursesTable
                    courses={group.items}
                    variant="assigned"
                    onPreview={setPreviewCourse}
                    onAction={unassignCourse}
                    onArchive={archiveCourse}
                    actionLoading={actionLoading}
                  />
                )}
                {group.key === "archivedTracks" && (
                  <TracksTable
                    tracks={group.items}
                    variant="archived"
                    coachGroup={coachGroup}
                    onPreview={setPreviewTrack}
                    onUnassign={unassignTrack}
                    onRestore={restoreTrack}
                    actionLoading={actionLoading}
                  />
                )}
                {group.key === "archivedCourses" && (
                  <CoursesTable
                    courses={group.items}
                    variant="archived"
                    onPreview={setPreviewCourse}
                    onAction={unassignCourse}
                    onRestore={restoreCourse}
                    actionLoading={actionLoading}
                  />
                )}
                {group.key === "catalogTracks" && (
                  <TracksTable
                    tracks={group.items}
                    variant="catalog"
                    coachGroup={coachGroup}
                    onPreview={setPreviewTrack}
                    onAssign={assignTrack}
                    actionLoading={actionLoading}
                  />
                )}
                {group.key === "catalogCourses" && (
                  <CoursesTable
                    courses={group.items}
                    variant="catalog"
                    onPreview={setPreviewCourse}
                    onAction={assignCourse}
                    actionLoading={actionLoading}
                  />
                )}
                {group.key === "myCourses" && <MyCoursesTable courses={group.items} />}
              </div>
            ))}
          </div>
        )}

        {!searching && activeTab === "assigned" && (
          <div className="space-y-8">
            {filteredAssignedTracks.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                  <Layers className="h-5 w-5 text-kf-red" /> Tracks
                </h2>
                <TracksTable
                  tracks={filteredAssignedTracks}
                  variant="assigned"
                  coachGroup={coachGroup}
                  onPreview={setPreviewTrack}
                  onUnassign={unassignTrack}
                  onReorder={reorderTrack}
                  onArchive={archiveTrack}
                  actionLoading={actionLoading}
                />
              </div>
            )}

            {filteredAssignedCourses.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-kf-red" />
                  {filteredAssignedTracks.length > 0 ? "Individual Courses" : "Courses"}
                </h2>
                <CoursesTable
                  courses={filteredAssignedCourses}
                  variant="assigned"
                  onPreview={setPreviewCourse}
                  onAction={unassignCourse}
                  onArchive={archiveCourse}
                  actionLoading={actionLoading}
                />
              </div>
            )}

            {filteredAssignedTracks.length === 0 && filteredAssignedCourses.length === 0 && (
              <EmptyState
                title={
                  assignedCount > 0 && filtersActive
                    ? "No matches"
                    : "Nothing assigned yet"
                }
                description={
                  assignedCount > 0 && filtersActive
                    ? "Try clearing filters or broadening your search."
                    : "Switch to the All Courses or All Tracks tab to assign content to your group."
                }
              />
            )}
          </div>
        )}

        {!searching && activeTab === "archived" && (
          <div className="space-y-8">
            {filteredArchivedTracks.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                  <Layers className="h-5 w-5 text-kf-red" /> Archived Tracks
                </h2>
                <TracksTable
                  tracks={filteredArchivedTracks}
                  variant="archived"
                  coachGroup={coachGroup}
                  onPreview={setPreviewTrack}
                  onUnassign={unassignTrack}
                  onRestore={restoreTrack}
                  actionLoading={actionLoading}
                />
              </div>
            )}

            {filteredArchivedCourses.length > 0 && (
              <div className="space-y-3">
                <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-kf-red" /> Archived Courses
                </h2>
                <CoursesTable
                  courses={filteredArchivedCourses}
                  variant="archived"
                  onPreview={setPreviewCourse}
                  onAction={unassignCourse}
                  onRestore={restoreCourse}
                  actionLoading={actionLoading}
                />
              </div>
            )}

            {filteredArchivedTracks.length === 0 && filteredArchivedCourses.length === 0 && (
              <EmptyState
                title="No matches"
                description="Try clearing filters or broadening your search."
              />
            )}
          </div>
        )}

        {!searching && activeTab === "tracks" && (
          <>
            {filteredAllTracks.length > 0 ? (
              <TracksTable
                tracks={filteredAllTracks}
                variant="all"
                coachGroup={coachGroup}
                onPreview={setPreviewTrack}
                onAssign={assignTrack}
                onUnassign={unassignTrack}
                isAssigned={trackIsAssigned}
                actionLoading={actionLoading}
              />
            ) : (
              <EmptyState
                icon={Layers}
                title={
                  allTracks.length > 0 && filtersActive
                    ? "No tracks match"
                    : "No tracks yet"
                }
                description={
                  allTracks.length > 0 && filtersActive
                    ? "Try clearing filters or search terms."
                    : "No learning tracks have been published yet."
                }
              />
            )}
          </>
        )}

        {!searching && activeTab === "courses" && (
          <>
            {filteredAllCourses.length > 0 ? (
              <CoursesTable
                courses={filteredAllCourses}
                variant="all"
                onPreview={setPreviewCourse}
                onAction={assignCourse}
                onUnassign={unassignCourse}
                assignmentFor={courseAssignment}
                actionLoading={actionLoading}
              />
            ) : (
              <EmptyState
                title={
                  allCourses.length > 0 && filtersActive
                    ? "No courses match"
                    : "No courses yet"
                }
                description={
                  allCourses.length > 0 && filtersActive
                    ? "Try clearing filters or search terms."
                    : "No courses have been shared to the catalog or assigned to your group yet."
                }
              />
            )}
          </>
        )}

        {!searching && activeTab === "mine" && (
          <>
            {filteredMyCourses.length > 0 ? (
              <MyCoursesTable courses={filteredMyCourses} />
            ) : (
              <EmptyState
                icon={BookOpen}
                title={
                  myCourses.length > 0 && filtersActive
                    ? "No matches"
                    : "You haven't created any courses yet"
                }
                description={
                  myCourses.length > 0 && filtersActive
                    ? "Try clearing filters or search terms."
                    : "Use Create Course to author your own course. Add at least one published lesson, publish the course, then open it and use “Assign to your groups” to make it available to your members."
                }
              />
            )}
          </>
        )}
      </div>

      <TrackPreviewDialog
        track={previewTrack}
        assigned={previewTrack ? trackIsAssigned(previewTrack.id) : false}
        onClose={() => setPreviewTrack(null)}
        onAssign={assignTrack}
        onUnassign={unassignTrack}
        onOpenCourse={openCourseFromTrack}
        actionLoading={actionLoading}
      />

      <CoursePreviewDialog
        course={previewCourse}
        open={!!previewCourse && !previewLesson}
        assigned={previewCourse ? courseIsAssigned(previewCourse.id) : false}
        onClose={() => setPreviewCourse(null)}
        onAssign={assignCourse}
        onUnassign={unassignCourse}
        onOpenLesson={(lesson) => setPreviewLesson({ lesson, course: previewCourse })}
        actionLoading={actionLoading}
      />

      <LessonPreviewDialog
        lesson={previewLesson?.lesson}
        courseTitle={previewLesson?.course?.title}
        onClose={() => {
          setPreviewLesson(null)
          setPreviewCourse(null)
        }}
        onBack={previewLesson?.course ? () => {
          const course = previewLesson.course
          setPreviewLesson(null)
          setPreviewCourse(course)
        } : undefined}
      />
    </CoachLayout>
  )
}
